import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import styled, { useTheme } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { getInboxConfig } from '../../actions/inbox_action'
import SupportEmail from '../Icons/SupportEmail'
import SupportCall from '../Icons/SupportCall'
import SupportSMS from '../Icons/SupportSMS'
import useWindowType from '../../custom-hooks/useWindowType'
import PencilIcon from '../Icons/Pencil'
import KenkoLoader from '../KenkoLoader'
import { inIframe } from '../../utils/helper'
import { isJson } from '../Common'

const DropdownModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0);
  z-index: 10001;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
`

// Custom Dialog container for better iframe positioning
const ContactUsDialogContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  width: 100%;
  height: 100vh;
  top: 0;
  left: 0;
  position: ${() => (inIframe() ? 'absolute' : 'fixed')};
  z-index: 20000;
  padding: 20px;
  box-sizing: border-box;
`

const ContactUsDialogBody = styled.div`
  box-sizing: border-box;
  position: relative;
  max-height: 90vh;
  margin: auto;
  border-radius: 12px;
  box-shadow: 0 20px 40px -4px rgba(0, 0, 0, 0.25),
    0 8px 32px -8px rgba(0, 0, 0, 0.15);
  background-color: ${props => props.theme.color.bg};
  transition: transform 0.3s ease;
  z-index: 1001;

  @media (max-width: ${props => props.theme.breakpoint.md}px) {
    margin: 20px;
    max-height: calc(100vh - 40px);
  }
`

const DropdownModalContainer = styled.div`
  position: absolute;
  background-color: ${props => props.theme.color.bg};
  border-radius: 12px;
  box-shadow: 0 20px 40px -4px rgba(0, 0, 0, 0.25),
    0 8px 32px -8px rgba(0, 0, 0, 0.15);
  min-width: 375px;
  max-height: 80vh;
  overflow-y: auto;
  z-index: 1001;

  @media (max-width: ${props => props.theme.breakpoint.md}px) {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    min-width: 280px;
    max-width: 90vw;
    max-height: 90vh;
    overflow-y: auto;
  }
`

const ContactContainer = styled.div`
  padding: 0px;
`

const ContactHeader = styled.div`
  margin-bottom: 24px;
`

const ContactHeaderContent = styled.div`
  display: flex;
  flex-direction: ${props => (props.showBackButton ? 'row' : 'column')};
  justify-content: space-between;
  @media (max-width: ${props => props.theme.breakpoint.md}px) {
    flex-direction: column;
  }
`

const ContactTitle = styled.h2`
  padding: 24px 24px 8px;
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.color.primaryText};
`

const ContactSubtitle = styled.p`
  padding: 0px 24px;
  font-size: 14px;
  color: ${props => props.theme.color.primaryText};
  opacity: 0.7;
  margin: 0;
  @media (max-width: ${props => props.theme.breakpoint.md}px) {
    flex-wrap: wrap;
    display: flex;
  }
`

const LocationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 16px 16px 24px;
  border-top: 1px solid ${props => props.theme.color.borderLight};

  @media (max-width: ${props => props.theme.breakpoint.md}px) {
    max-height: 250px;
    overflow-y: auto;
  }
`

const RegionGroup = styled.div`
  margin-bottom: 16px;
`

const RegionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.theme.color.secondaryText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  padding: 0 4px;
`

const LocationItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0px;
  cursor: pointer;
  border-radius: 8px;
  margin-bottom: 4px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${props => props.theme.color.borderLight + '40'};
  }

  &:active {
    background-color: ${props => props.theme.color.borderLight + '60'};
  }
`

const LocationIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;

  svg {
    width: 16px;
    height: 16px;
    fill: ${props => props.theme.color.primaryText + '80'};
  }
`

const LocationName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.color.primaryText};
`

const ContactOptions = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: row;
  padding: 0px 24px 24px;
  @media (max-width: ${props => props.theme.breakpoint.md}px) {
    gap: 12px;
    padding: 0px 24px 24px 24px;
  }
`

const ContactOption = styled.div`
  flex: 1;
  text-align: center;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  background-color: ${props => props.theme.color.borderDark + '10'};
  transition: all 0.2s ease;
  min-width: 120px;
  height: 140px;
  box-shadow: none;
  border-radius: 12px;
  position: relative;
  &:hover {
    ${props =>
      !props.disabled &&
      `
      border-color: ${props.theme.color.brand};
    `}
  }

  @media (max-width: ${props => props.theme.breakpoint.md}px) {
    background-color: ${props => props.theme.color.bg};
    height: 110px;
  }
`

const ContactOptionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background-color: ${props => props.theme.color.bg};
  padding: 12px;
  position: absolute;
  left: 0;
  right: 0;
`

const IconContainer = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: ${props => props.theme.color.brand + '20'};
  border: 1px solid ${props => props.theme.color.brand};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
`

const ContactLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.color.primaryText};
  margin-bottom: 8px;
`

const ContactAction = styled.div`
  font-size: 12px;
  color: ${props => props.theme.color.secondaryText + '80'};
  border: 1px solid ${props => props.theme.color.borderDark + '30'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  padding-left: 8px;
  padding-right: 8px;
  padding-top: 16px;
  padding-bottom: 4px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  svg {
    width: 12px;
    height: 12px;
  }
`

const UnavailableText = styled.div`
  font-size: 12px;
  color: ${props => props.theme.color.primaryText};
  border: 1px solid ${props => props.theme.color.borderDark + '30'};
  opacity: 0.5;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  padding-left: 8px;
  padding-right: 8px;
  padding-top: 16px;
  padding-bottom: 4px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`

const UnderlineText = styled.span`
  text-decoration: underline;
  color: ${props => props.theme.color.primaryText};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  font-weight: 600;

  svg {
    width: 12px;
    height: 12px;
  }

  span {
    margin-left: 2px;
  }

  svg {
    margin-left: 4px;
    stroke: ${props => props.theme.color.primaryText};
  }
`

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
  </svg>
)

// Location pin icon component
const LocationPinIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <g opacity="0.6" clipPath="url(#clip0_2568_132998)">
      <path
        d="M8.00016 1.3335C5.42016 1.3335 3.3335 3.42016 3.3335 6.00016C3.3335 9.50016 8.00016 14.6668 8.00016 14.6668C8.00016 14.6668 12.6668 9.50016 12.6668 6.00016C12.6668 3.42016 10.5802 1.3335 8.00016 1.3335ZM4.66683 6.00016C4.66683 4.16016 6.16016 2.66683 8.00016 2.66683C9.84016 2.66683 11.3335 4.16016 11.3335 6.00016C11.3335 7.92016 9.4135 10.7935 8.00016 12.5868C6.6135 10.8068 4.66683 7.90016 4.66683 6.00016Z"
        fill="#1B1B1A"
      />
      <path
        d="M8.00016 7.66683C8.92064 7.66683 9.66683 6.92064 9.66683 6.00016C9.66683 5.07969 8.92064 4.3335 8.00016 4.3335C7.07969 4.3335 6.3335 5.07969 6.3335 6.00016C6.3335 6.92064 7.07969 7.66683 8.00016 7.66683Z"
        fill="#1B1B1A"
      />
    </g>
    <defs>
      <clipPath id="clip0_2568_132998">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg>
)

const ContactUsModal = ({ isOpen, onClose, triggerRef }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const [sizeType] = useWindowType()
  const isMobile = sizeType === 'sm'
  const [copiedStates, setCopiedStates] = useState({
    email: false,
    call: false,
    sms: false
  })
  const [selectedCenter, setSelectedCenter] = useState(null)
  const [currentStep, setCurrentStep] = useState('location') // 'location' or 'contact'
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 })
  const [frameRect, setFrameRect] = useState(null)
  const modalRef = useRef(null)

  const regions = useSelector(
    state => state.centerreducer.regionsDetails?.regions ?? []
  )
  const { config, loading } = useSelector(state => state.inboxReducer)
  const isLoggedIn = useSelector(state => state.isLoggedReducer)
  const customerProfile = useSelector(
    state => state.accountreducer.customerProfile
  )

  // Get all available centers from all regions
  const allCenters = useMemo(() => {
    if (!regions || regions.length === 0) return []

    return regions.reduce((acc, region) => {
      const regionCenters =
        region.centers?.map(center => ({
          ...center,
          regionName: region.name
        })) || []
      return [...acc, ...regionCenters]
    }, [])
  }, [regions])

  const centersByRegion = useMemo(() => {
    const grouped = {}
    allCenters.forEach(center => {
      if (!grouped[center.regionName]) {
        grouped[center.regionName] = []
      }
      grouped[center.regionName].push(center)
    })
    return grouped
  }, [allCenters])

  useEffect(() => {
    if (isOpen) {
      setCurrentStep('location')
      if (allCenters.length === 1) {
        setSelectedCenter(allCenters[0])
        setCurrentStep('contact')
      } else if (allCenters.length > 1 && isLoggedIn && customerProfile?.data) {
        const homeCenter =
          customerProfile.data.home_center ?? customerProfile.data.center
        if (homeCenter) {
          const matchingCenter = allCenters.find(
            center => center.id === homeCenter.id
          )
          if (matchingCenter) {
            setSelectedCenter(matchingCenter)
            setCurrentStep('contact')
          }
        }
      } else if (allCenters.length > 1 && !isLoggedIn) {
        setSelectedCenter(allCenters[0])
        setCurrentStep('contact')
      }
    }
  }, [isOpen, allCenters, isLoggedIn, customerProfile])

  const centerId = selectedCenter?.uuid ?? null

  useEffect(() => {
    if (currentStep === 'contact' && centerId) {
      dispatch(getInboxConfig(centerId))
    }
  }, [currentStep, centerId, dispatch])

  useEffect(() => {
    if (isOpen && sizeType !== 'sm') {
      let targetElement = null

      if (triggerRef?.current) {
        targetElement = triggerRef.current
      }

      if (!targetElement) {
        console.warn('Contact button element not found for modal positioning')
        return
      }

      const targetRect = targetElement.getBoundingClientRect()

      const modalWidth = 375
      const modalHeight = 300
      const padding = 8

      let top = targetRect.bottom + padding
      let left = targetRect.left + targetRect.width / 2 - modalWidth / 2

      // Adjust if modal would go off screen
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      // Adjust horizontal position if needed
      if (left < padding) {
        left = padding
      }
      if (left + modalWidth > viewportWidth - padding) {
        left = viewportWidth - modalWidth - padding
      }

      // Adjust vertical position if needed
      if (top + modalHeight > viewportHeight - padding) {
        top = targetRect.top - modalHeight - padding
      }

      setModalPosition({ top, left })
    }
  }, [isOpen, triggerRef, sizeType])

  // Iframe positioning handler
  const receiveRect = useCallback(e => {
    const { type, message } = isJson(e.data) ? JSON.parse(e.data) : e.data
    if (type === 'SeeClientRect') {
      setFrameRect(message?.rect?.top)
    }
  }, [])

  // Iframe positioning logic - runs when frameRect changes
  useEffect(() => {
    if (modalRef.current !== null && frameRect !== null) {
      // Apply transform based on iframe position relative to viewport
      if (frameRect < 0) {
        const transformValue = `translateY(${-frameRect}px)`
        modalRef.current.style.transform = transformValue
      } else {
        modalRef.current.style.transform = `translateY(0px)`
      }
    }
  }, [frameRect])

  // Setup iframe positioning and scroll handling
  useEffect(() => {
    if (inIframe() && isOpen) {
      // Request initial iframe position
      window.parent.postMessage(
        JSON.stringify({
          type: 'ReceiveClientRect',
          message: null
        }),
        localStorage.getItem('parentOrigin')
      )

      // Listen for scroll updates from parent
      window.addEventListener('message', receiveRect, false)

      return () => {
        window.removeEventListener('message', receiveRect, false)
      }
    } else if (!inIframe() && isOpen) {
      // Handle non-iframe positioning
      const rect = document.getElementById('root')?.getBoundingClientRect()
      if (rect && modalRef.current !== null) {
        const offset = rect.top
        modalRef.current.style.transform = `translateY(${-offset}px)`
      }

      // Handle scroll for non-iframe
      const handleScroll = () => {
        const rect = document.getElementById('root')?.getBoundingClientRect()
        if (rect && modalRef.current !== null) {
          const offset = rect.top
          modalRef.current.style.transform = `translateY(${-offset}px)`
        }
      }

      window.addEventListener('scroll', handleScroll)

      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [isOpen, receiveRect])

  const handleLocationItemClick = center => {
    setSelectedCenter(center)
    localStorage.setItem('selected_center_id', center.id.toString())
    setCurrentStep('contact')
  }

  const handleBackClick = () => {
    setCurrentStep('location')
  }

  const handleOverlayClick = e => {
    if (
      e.target.closest('[data-dropdown-list]') ||
      e.target.closest('[data-dropdown-item]')
    ) {
      return
    }

    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const copyToClipboard = async (text, type) => {
    try {
      const isChromeBrowser =
        navigator.userAgent.includes('Chrome') ||
        navigator.userAgent.includes('Chromium') ||
        navigator.userAgent.includes('Edge') ||
        navigator.userAgent.includes('Safari')

      if (navigator.clipboard && window.isSecureContext) {
        if (isChromeBrowser && navigator.permissions) {
          try {
            const permission = await navigator.permissions.query({
              name: 'clipboard-write'
            })

            if (
              permission.state === 'granted' ||
              permission.state === 'prompt'
            ) {
              await navigator.clipboard.writeText(text)
              setCopiedStates(prev => ({ ...prev, [type]: true }))
              setTimeout(() => {
                setCopiedStates(prev => ({ ...prev, [type]: false }))
              }, 2000)
              return
            }
          } catch (permissionError) {
            console.warn('Clipboard permission check failed:', permissionError)
          }
        } else {
          try {
            await navigator.clipboard.writeText(text)
            setCopiedStates(prev => ({ ...prev, [type]: true }))
            setTimeout(() => {
              setCopiedStates(prev => ({ ...prev, [type]: false }))
            }, 2000)
            return
          } catch (clipboardError) {
            console.warn('Clipboard API failed:', clipboardError)
          }
        }
      }

      // Fallback method using document.execCommand
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      const successful = document.execCommand('copy')
      document.body.removeChild(textArea)

      if (successful) {
        setCopiedStates(prev => ({ ...prev, [type]: true }))
        setTimeout(() => {
          setCopiedStates(prev => ({ ...prev, [type]: false }))
        }, 2000)
      } else {
        throw new Error('Copy command failed')
      }
    } catch (err) {
      console.error('Failed to copy text:', err)
    }
  }

  const handleEmailClick = () => {
    const email = config?.data?.email_inbox?.from_email
    if (!email) return

    if (isMobile) {
      window.location.href = `mailto:${email}`
    } else {
      copyToClipboard(email, 'email')
    }
  }

  const handleCallClick = () => {
    const phone = config?.data?.voice_inbox?.phone
    const prefix = config?.data?.voice_inbox?.phone_prefix
    if (!phone || !prefix) return

    const fullNumber = `${prefix}${phone}`
    if (isMobile) {
      window.location.href = `tel:${fullNumber}`
    } else {
      copyToClipboard(fullNumber, 'call')
    }
  }

  const handleSMSClick = () => {
    const phone = config?.data?.sms_inbox?.phone
    const prefix = config?.data?.sms_inbox?.phone_prefix
    if (!phone || !prefix) return

    const fullNumber = `${prefix}${phone}`
    if (isMobile) {
      window.location.href = `sms:${fullNumber}`
    } else {
      copyToClipboard(fullNumber, 'sms')
    }
  }

  const handleEmailCardClick = () => {
    const email = config?.data?.email_inbox?.from_email
    if (!email) return
    window.location.href = `mailto:${email}`
  }

  const handleCallCardClick = () => {
    const phone = config?.data?.voice_inbox?.phone
    const prefix = config?.data?.voice_inbox?.phone_prefix
    if (!phone || !prefix) return
    const fullNumber = `${prefix}${phone}`
    window.location.href = `tel:${fullNumber}`
  }

  const handleSMSCardClick = () => {
    const phone = config?.data?.sms_inbox?.phone
    const prefix = config?.data?.sms_inbox?.phone_prefix
    if (!phone || !prefix) return
    const fullNumber = `${prefix}${phone}`
    window.location.href = `sms:${fullNumber}`
  }

  // Desktop-specific handlers for copy button clicks
  const handleEmailCopyClick = e => {
    e.stopPropagation()
    const email = config?.data?.email_inbox?.from_email
    if (!email) return
    copyToClipboard(email, 'email')
  }

  const handleCallCopyClick = e => {
    e.stopPropagation()
    const phone = config?.data?.voice_inbox?.phone
    const prefix = config?.data?.voice_inbox?.phone_prefix
    if (!phone || !prefix) return
    const fullNumber = `${prefix}${phone}`
    copyToClipboard(fullNumber, 'call')
  }

  const handleSMSCopyClick = e => {
    e.stopPropagation()
    const phone = config?.data?.sms_inbox?.phone
    const prefix = config?.data?.sms_inbox?.phone_prefix
    if (!phone || !prefix) return
    const fullNumber = `${prefix}${phone}`
    copyToClipboard(fullNumber, 'sms')
  }

  const emailEnabled =
    config?.data?.email_inbox &&
    config?.data?.email_inbox !== null &&
    config?.data?.email_inbox?.email_forwarding_enabled
  const callEnabled =
    config?.data?.voice_inbox && config?.data?.voice_inbox !== null
  const smsEnabled = config?.data?.sms_inbox && config?.data?.sms_inbox !== null
  const emailExists = !!config?.data?.email_inbox?.from_email
  const callExists = !!config?.data?.voice_inbox?.phone
  const smsExists = !!config?.data?.sms_inbox?.phone

  const hasAnyOption = emailExists || callExists || smsExists

  if (!isOpen) return null

  // Use dropdown positioning for desktop, modal for mobile
  if (sizeType === 'sm' || !triggerRef?.current) {
    return (
      <ContactUsDialogContainer onClick={handleOverlayClick}>
        <ContactUsDialogBody ref={modalRef}>
          <ContactContainer>
            <ContactHeader>
              <ContactHeaderContent showBackButton={currentStep === 'contact'}>
                <div>
                  <ContactTitle>Contact us</ContactTitle>
                  <ContactSubtitle>
                    {currentStep === 'location'
                      ? 'Pick location so we can connect you with the right team.'
                      : `Reach out to our team at `}
                    {currentStep === 'contact' && (
                      <UnderlineText
                        onClick={handleBackClick}
                        style={{
                          marginLeft: '2px'
                        }}
                      >
                        {selectedCenter?.branch}
                        <PencilIcon stroke={theme.color.primaryText} />
                      </UnderlineText>
                    )}
                  </ContactSubtitle>
                </div>
              </ContactHeaderContent>
            </ContactHeader>

            {currentStep === 'location' && (
              <LocationList>
                {Object.entries(centersByRegion).map(
                  ([regionName, centers]) => (
                    <RegionGroup key={regionName}>
                      <RegionTitle>{regionName}</RegionTitle>
                      {centers.map(center => (
                        <LocationItem
                          key={center.id}
                          onClick={() => handleLocationItemClick(center)}
                        >
                          <LocationIcon>
                            <LocationPinIcon />
                          </LocationIcon>
                          <LocationName>{center.branch}</LocationName>
                        </LocationItem>
                      ))}
                    </RegionGroup>
                  )
                )}
              </LocationList>
            )}

            {currentStep === 'contact' && loading ? (
              <KenkoLoader />
            ) : currentStep === 'contact' && !hasAnyOption ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '20px',
                  color: theme.color.primaryText
                }}
              >
                Contact options are not available at the moment.
              </div>
            ) : (
              currentStep === 'contact' && (
                <ContactOptions>
                  {emailEnabled && (
                    <ContactOption
                      disabled={!emailEnabled}
                      onClick={emailEnabled ? handleEmailClick : undefined}
                    >
                      <ContactOptionContent>
                        <IconContainer>
                          <SupportEmail fill={theme.color.brand} />
                        </IconContainer>
                        <ContactLabel>Email us</ContactLabel>
                      </ContactOptionContent>
                    </ContactOption>
                  )}

                  {callEnabled && (
                    <ContactOption
                      disabled={!callEnabled}
                      onClick={callEnabled ? handleCallClick : undefined}
                    >
                      <ContactOptionContent>
                        <IconContainer>
                          <SupportCall fill={theme.color.brand} />
                        </IconContainer>
                        <ContactLabel>Call us</ContactLabel>
                      </ContactOptionContent>
                    </ContactOption>
                  )}

                  {smsEnabled && (
                    <ContactOption
                      disabled={!smsEnabled}
                      onClick={smsEnabled ? handleSMSClick : undefined}
                    >
                      <ContactOptionContent>
                        <IconContainer>
                          <SupportSMS fill={theme.color.brand} />
                        </IconContainer>
                        <ContactLabel>Message us</ContactLabel>
                      </ContactOptionContent>
                    </ContactOption>
                  )}
                </ContactOptions>
              )
            )}
          </ContactContainer>
        </ContactUsDialogBody>
      </ContactUsDialogContainer>
    )
  }

  // Desktop dropdown positioning
  return (
    <DropdownModalOverlay onClick={handleOverlayClick}>
      <DropdownModalContainer
        style={{
          top: modalPosition.top,
          left: modalPosition.left
        }}
      >
        <ContactContainer>
          <ContactHeader>
            <ContactHeaderContent showBackButton={currentStep === 'contact'}>
              <div>
                <ContactTitle>Contact us</ContactTitle>
                <ContactSubtitle>
                  {currentStep === 'location'
                    ? 'Pick location so we can connect you with the right team.'
                    : `Reach out to our team at `}
                  {currentStep === 'contact' && (
                    <UnderlineText onClick={handleBackClick}>
                      {selectedCenter?.branch}
                      <PencilIcon stroke={theme.color.primaryText} />
                    </UnderlineText>
                  )}
                </ContactSubtitle>
              </div>
            </ContactHeaderContent>
          </ContactHeader>

          {currentStep === 'location' && (
            <LocationList>
              {Object.entries(centersByRegion).map(([regionName, centers]) => (
                <RegionGroup key={regionName}>
                  <RegionTitle>{regionName}</RegionTitle>
                  {centers.map(center => (
                    <LocationItem
                      key={center.id}
                      onClick={() => handleLocationItemClick(center)}
                    >
                      <LocationIcon>
                        <LocationPinIcon />
                      </LocationIcon>
                      <LocationName>{center.branch}</LocationName>
                    </LocationItem>
                  ))}
                </RegionGroup>
              ))}
            </LocationList>
          )}

          {currentStep === 'contact' && (
            <>
              {loading ? (
                <KenkoLoader />
              ) : !hasAnyOption ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: theme.color.primaryText
                  }}
                >
                  Contact options are not available at the moment.
                </div>
              ) : (
                <ContactOptions>
                  {emailEnabled && (
                    <ContactOption
                      disabled={!emailEnabled}
                      onClick={emailEnabled ? handleEmailCardClick : undefined}
                    >
                      {emailExists ? (
                        <ContactAction onClick={handleEmailCopyClick}>
                          <CopyIcon />
                          {copiedStates.email ? 'Copied' : 'Copy'}
                        </ContactAction>
                      ) : (
                        <UnavailableText>Unavailable</UnavailableText>
                      )}
                      <ContactOptionContent>
                        <IconContainer>
                          <SupportEmail fill={theme.color.brand} />
                        </IconContainer>
                        <ContactLabel>Email us</ContactLabel>
                      </ContactOptionContent>
                    </ContactOption>
                  )}

                  {callEnabled && (
                    <ContactOption
                      disabled={!callEnabled}
                      onClick={callEnabled ? handleCallCardClick : undefined}
                    >
                      {callExists ? (
                        <ContactAction onClick={handleCallCopyClick}>
                          <CopyIcon />
                          {copiedStates.call ? 'Copied' : 'Copy'}
                        </ContactAction>
                      ) : (
                        <UnavailableText>Unavailable</UnavailableText>
                      )}
                      <ContactOptionContent>
                        <IconContainer>
                          <SupportCall fill={theme.color.brand} />
                        </IconContainer>
                        <ContactLabel>Call us</ContactLabel>
                      </ContactOptionContent>
                    </ContactOption>
                  )}

                  {smsEnabled && (
                    <ContactOption
                      disabled={!smsEnabled}
                      onClick={smsEnabled ? handleSMSCardClick : undefined}
                    >
                      {smsExists ? (
                        <ContactAction onClick={handleSMSCopyClick}>
                          <CopyIcon />
                          {copiedStates.sms ? 'Copied' : 'Copy'}
                        </ContactAction>
                      ) : (
                        <UnavailableText>Unavailable</UnavailableText>
                      )}
                      <ContactOptionContent>
                        <IconContainer>
                          <SupportSMS fill={theme.color.brand} />
                        </IconContainer>
                        <ContactLabel>Message us</ContactLabel>
                      </ContactOptionContent>
                    </ContactOption>
                  )}
                </ContactOptions>
              )}
            </>
          )}
        </ContactContainer>
      </DropdownModalContainer>
    </DropdownModalOverlay>
  )
}

export default ContactUsModal
